import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';

import {NumberInput, Select} from '@/components/common/Input';
import type {AfriexAdminAmount} from '@/types';
import {AfriexCurrencyCode} from '@/types';

interface CurrencyPickersProps {
  onChange: (value: AfriexAdminAmount) => void;
  /** Called when the amount box is emptied, which `onChange` never reports. */
  onClear?: () => void;
  value?: AfriexAdminAmount;
  label?: string;
  fixedCurrency?: AfriexCurrencyCode;
  containerStyle?: string | undefined;
  min?: number;
  max?: number;
  /** Test hook placed on the amount `<input>` itself. */
  dataCy?: string;
  /**
   * Narrow the currency picker. Used by the marketplace ask form, whose volume
   * may only be a currency the marketplace can pay out on. Omitted means every
   * currency in the enum.
   */
  allowedCurrencies?: AfriexCurrencyCode[];
}

const mapOptionsToList = (options: AfriexCurrencyCode[]) =>
  options.map((currency: AfriexCurrencyCode) => ({
    key: currency,
    value: currency,
  }));

const AmountInput = ({
  label,
  onChange,
  onClear,
  value,
  fixedCurrency,
  containerStyle,
  min,
  max,
  dataCy,
  allowedCurrencies,
}: CurrencyPickersProps): ReactElement => {
  const [currency, setCurrency] = useState<AfriexCurrencyCode>(
    AfriexCurrencyCode.NGN,
  );

  useEffect(() => {
    if (fixedCurrency) {
      setCurrency(fixedCurrency);
    }
  }, [fixedCurrency]);

  useEffect(() => {
    if (value?.currency) {
      setCurrency(value.currency);
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAmount = event.target.value;
    if (!selectedAmount) {
      onClear?.();
      return;
    }
    if (!currency) {
      return;
    }
    onChange({
      amount: Number(selectedAmount),
      currency: currency,
    });
  };
  const handleCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedCurrency = event.target.value as AfriexCurrencyCode;
    if (!selectedCurrency) {
      return;
    }
    setCurrency(selectedCurrency);
    onChange({
      currency: selectedCurrency,
    } as AfriexAdminAmount);
  };

  const amountInputProps = {
    ...(containerStyle && {className: containerStyle}),
  };
  // Keep the current selection in the list even when it is no longer allowed,
  // so editing an ask created before the channel map changed cannot render a
  // select whose value has no matching option.
  const currencyOptions = mapOptionsToList([
    ...new Set([
      ...(allowedCurrencies ?? Object.values(AfriexCurrencyCode)),
      currency,
    ]),
  ]);
  return (
    <div {...amountInputProps}>
      <label
        htmlFor="price"
        className="block text-sm font-medium leading-6 text-gray-700 dark:text-gray-200"
      >
        {label}
      </label>
      <div className="relative mt-1 rounded-md shadow-sm">
        <NumberInput
          name="price"
          id="price"
          data-cy={dataCy}
          value={value?.amount || ''}
          min={min}
          max={max}
          className="block w-full rounded-md border-0 p-2.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 dark:bg-transparent dark:text-gray-100 dark:ring-gray-600 dark:placeholder:text-gray-500 dark:focus:ring-indigo-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          placeholder="0.00"
          onValueChange={(rawValue) => {
            handleChange({
              target: {
                name: 'price',
                value: rawValue,
              },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
        />
        <div className="absolute inset-y-0 right-0 flex items-center">
          <label htmlFor="currency" className="sr-only">
            Currency
          </label>
          <Select
            id="currency"
            name="currency"
            onChange={handleCurrencyChange}
            value={currency}
            dataCy={dataCy ? `${dataCy}-currency` : undefined}
            disabled={!!fixedCurrency}
            bodyClassName="border-l border-t-0 border-b-0 bg-transparent pl-2 text-gray-500 focus:ring-0 sm:text-sm rounded-none"
            options={currencyOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default AmountInput;
